import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Grid,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Star,
  Calendar,
  Clock,
  Share2,
  ArrowRight,
  Newspaper,
} from "lucide-react";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { CommonButton, CommonChip } from "../../../../Components/ui";

const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const calculateReadingTime = (text) => {
  if (!text) return "1 min read";
  const wordsPerMinute = 200;
  const wordCount = text.trim().split(/\s+/).length;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  return `${Math.max(1, minutes)} min read`;
};

const isFeaturedArticle = (item) => {
  if (!item) return false;
  return (
    item.featured === true ||
    item.featured === 1 ||
    item.featured === "1" ||
    item.featured === "true" ||
    item.isFeatured === true ||
    item.isFeatured === 1 ||
    item.isFeatured === "1" ||
    item.isFeatured === "true"
  );
};

const NewsFeaturedCard = ({
  article,
  onRead,
  onShare,
  formatTimeAgo,
  formatPublishDate,
}) => {
  if (!article) return null;

  const {
    title,
    description,
    imageUrl,
    updatedOn,
    createdOn,
  } = article;

  const snippet = stripHtml(description);
  const displayDate = updatedOn || createdOn;
  const readingTime = calculateReadingTime(snippet);
  const isFeatured = isFeaturedArticle(article);

  return (
    <Card
      className="news-featured-card"
      sx={{
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        overflow: "hidden",
        mb: 4,
        position: "relative",
      }}
    >
      <Grid container>
        {/* Left Side / Cover Image Banner */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              position: "relative",
              height: { xs: 240, md: 380 },
              width: "100%",
              overflow: "hidden",
              bgcolor: colors.slate[100],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {imageUrl ? (
              <Box
                component="img"
                src={imageUrl}
                alt={title}
                className="featured-cover-image"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transition: "transform 0.4s ease-in-out",
                }}
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                  color: colors.slate[400],
                }}
              >
                <Newspaper size={48} strokeWidth={1.5} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Autobet Official Update
                </Typography>
              </Box>
            )}

            {/* Featured Badge Overlay */}
            <Box
              sx={{
                position: "absolute",
                top: 16,
                left: 16,
                zIndex: 2,
              }}
            >
              <CommonChip
                label="Recent"
                startIcon={<Star size={13} fill={colors.amber[500]} color={colors.amber[500]} />}
                size="small"
                sx={{
                  bgcolor: colors.neutral.white,
                  color: colors.amber[800],
                  fontWeight: 700,
                  boxShadow: customShadows.card,
                  border: `1px solid ${colors.amber[200]}`,
                }}
              />
            </Box>
          </Box>
        </Grid>

        {/* Right Side / Article Headline Content */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: { xs: 3, sm: 4 },
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box>
              {/* Meta Tags Row */}
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                flexWrap="wrap"
                sx={{ mb: 2, gap: 1 }}
              >
                {isFeatured ? (
                  <CommonChip
                    label="Featured Story"
                    startIcon={<Star size={12} fill={colors.amber[500]} color={colors.amber[500]} />}
                    size="small"
                    sx={{
                      bgcolor: colors.amber[50],
                      color: colors.amber[800],
                      fontWeight: 700,
                      border: `1px solid ${colors.amber[200]}`,
                    }}
                  />
                ) : (
                  <CommonChip
                    label="Official Announcement"
                    size="small"
                    sx={{
                      bgcolor: colors.blue[50],
                      color: colors.primary.main,
                      fontWeight: 700,
                      border: `1px solid ${colors.blue[200]}`,
                    }}
                  />
                )}

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: colors.slate[500] }}>
                  <Calendar size={13} color={colors.slate[400]} />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {formatPublishDate(displayDate)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: colors.slate[500] }}>
                  <Clock size={13} color={colors.slate[400]} />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {formatTimeAgo(displayDate)}
                  </Typography>
                </Box>

                <Typography variant="caption" sx={{ color: colors.slate[400], fontWeight: 500 }}>
                  • {readingTime}
                </Typography>
              </Stack>

              {/* Title */}
              <Typography
                variant="h4"
                onClick={() => onRead(article)}
                sx={{
                  fontWeight: 800,
                  color: colors.slate[900],
                  fontSize: { xs: "1.35rem", sm: "1.65rem" },
                  lineHeight: 1.3,
                  mb: 1.75,
                  cursor: "pointer",
                  transition: "color 0.2s ease",
                  "&:hover": {
                    color: colors.primary.main,
                  },
                }}
              >
                {title}
              </Typography>

              {/* Snippet Excerpt */}
              <Typography
                variant="body1"
                sx={{
                  color: colors.slate[600],
                  lineHeight: 1.65,
                  fontSize: "0.95rem",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  mb: 3,
                }}
              >
                {snippet}
              </Typography>
            </Box>

            {/* Bottom Actions Bar */}
            <Box
              sx={{
                pt: 2.5,
                borderTop: `1px solid ${colors.dashboard.tableBorder}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              <CommonButton
                variant="primary"
                size="medium"
                endIcon={<ArrowRight size={16} />}
                onClick={() => onRead(article)}
              >
                Read Full Story
              </CommonButton>

              <Tooltip title="Share this article link">
                <IconButton
                  onClick={() => onShare(article)}
                  sx={{
                    bgcolor: colors.slate[50],
                    color: colors.slate[600],
                    border: `1px solid ${colors.slate[200]}`,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: colors.blue[50],
                      color: colors.primary.main,
                      borderColor: colors.blue[200],
                    },
                  }}
                >
                  <Share2 size={17} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};

NewsFeaturedCard.propTypes = {
  article: PropTypes.object,
  onRead: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired,
  formatTimeAgo: PropTypes.func.isRequired,
  formatPublishDate: PropTypes.func.isRequired,
};

export default NewsFeaturedCard;
